import { Ban } from "lucide-react"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { formatearImporte } from "./PanelRubros"

/**
 * Tabla compartida por tesorería y por las dos bandejas de cobrar y pagar.
 *
 * ⚠ **Sin manejador no hay botón.** La columna de acción depende de `onAccion`, no
 * de `accion`: una etiqueta sin nada que hacer pinta un botón que se pulsa y no pasa
 * nada, y eso es peor que no ofrecerlo — el operador cree que registró el cobro. Fue
 * un defecto real de esta tabla: la bandeja de CxC pasaba `accion="Cobrar"` sin
 * `onAccion` y el clic caía al vacío sin que ninguna prueba lo notara.
 *
 * ⚠ **Una fila saldada no ofrece acción de cobro.** No es que el botón esté
 * deshabilitado: no está. Un botón gris invita a pulsarlo, y quien lo pulse recibirá
 * un `MONTO_EXCEDE_SALDO` que no explica nada — la fila ya está saldada, no hay nada
 * que cobrar.
 *
 * ⚠ **Una transacción anulada sigue visible y marcada.** Nunca se borra, así que
 * tampoco desaparece de la vista: ocultarla haría que el ledger de pantalla y el de
 * la base contaran cosas distintas, y el día que un saldo no cuadra lo único que
 * sirve es la fila que explica el movimiento.
 *
 * Todo importe lleva `tabular-nums` y su signo, por lo mismo que en `PanelRubros`.
 */

export type FilaSaldo = {
  id: number
  concepto: string
  monto: string
  aplicado: string
  moneda: "USD" | "BS"
  /** `true` cuando ya no queda nada por cobrar o pagar. */
  saldada: boolean
  /** Sólo en el ledger de banco: una transacción revertida. */
  anulada?: boolean
  /** Una marca al lado del concepto —«Cobrada en el 2.0»—, con su explicación. */
  insignia?: { texto: string; tono: Tono; titulo?: string }
  /**
   * La acción de ESTA fila, si no es la de la tabla (18/10): una CxP espejo del 2.0 se
   * paga con una orden —«Generar orden de pago»— y una hecha a mano se abona. `null`
   * quita el botón de esta fila.
   */
  accion?: string | null
}

type Props = {
  filas: FilaSaldo[]
  /** Etiqueta de la acción: «Cobrar» en CxC, «Abonar» en CxP. Ausente en tesorería. */
  accion?: string
  onAccion?: (id: number) => void
  /**
   * Una segunda acción, por el mismo criterio que la primera: sin manejador no hay
   * botón. La usa «Por pagar» para los soportes, y a diferencia de la de cobro **se
   * ofrece también en una fila saldada**: el documento que motiva la deuda hace falta
   * igual —o más— después de pagarla.
   */
  accionSecundaria?: string
  onAccionSecundaria?: (id: number) => void
  /** Cómo se llama lo ya aplicado: «Cobrado» en CxC, «Pagado» en CxP. */
  etiquetaAplicado?: string
  /** Con él, el concepto es un botón que abre la ficha de la fila (16/10). */
  onAbrir?: (id: number) => void
}

export function saldoDeFila(fila: FilaSaldo): string {
  return (Number(fila.monto) - Number(fila.aplicado)).toFixed(2)
}

export function TablaSaldos({
  filas,
  accion,
  onAccion,
  accionSecundaria,
  onAccionSecundaria,
  etiquetaAplicado = "Aplicado",
  onAbrir,
}: Props) {
  // Las dos cosas, o ninguna. Una etiqueta sin manejador no es una acción.
  const ofreceAccion = Boolean(accion) && onAccion !== undefined
  const ofreceSecundaria = Boolean(accionSecundaria) && onAccionSecundaria !== undefined
  const hayColumna = ofreceAccion || ofreceSecundaria

  if (filas.length === 0) {
    return (
      <p className="superficie border-dashed p-6 text-center" style={{ color: "var(--fg-muted)" }}>
        No hay nada aquí todavía.
      </p>
    )
  }

  return (
    <Tabla>
      <Cabecera>
        <Th>Concepto</Th>
        <Th derecha>Monto</Th>
        <Th derecha>{etiquetaAplicado}</Th>
        <Th derecha>Saldo</Th>
        {hayColumna && <Th />}
      </Cabecera>
      <Cuerpo>
        {filas.map((fila) => (
          <Fila key={fila.id} data-testid={`fila-${fila.id}`}>
            <Td>
              <span className="flex items-center gap-2">
                {fila.anulada && (
                  <>
                    <Ban size={14} aria-hidden={true} style={{ color: "var(--destructive)" }} />
                    {/* El icono no basta: el estado se escribe. */}
                    <span
                      data-testid={`anulada-${fila.id}`}
                      style={{ color: "var(--destructive)" }}
                    >
                      ANULADA
                    </span>
                  </>
                )}
                {onAbrir ? (
                  <button
                    type="button"
                    onClick={() => onAbrir(fila.id)}
                    className="foco-cristal text-left underline decoration-dotted underline-offset-4 hover:decoration-solid"
                  >
                    {fila.concepto}
                  </button>
                ) : (
                  <span>{fila.concepto}</span>
                )}
                {fila.insignia && (
                  <span title={fila.insignia.titulo} className="shrink-0">
                    <Insignia tono={fila.insignia.tono}>{fila.insignia.texto}</Insignia>
                  </span>
                )}
              </span>
            </Td>
            <Td derecha numero>
              {formatearImporte(fila.monto, fila.moneda, "")}
            </Td>
            <Td derecha numero>
              {/* ⚠ EN POSITIVO (16/10, pedido del usuario): «-400,00» se leía como una
                  deuda o un descuadre. La columna se llama «Cobrado» o «Pagado» y el
                  saldo de al lado ya dice lo que falta. */}
              {formatearImporte(fila.aplicado, fila.moneda, "")}
            </Td>
            <Td derecha numero data-testid={`saldo-${fila.id}`} className="font-medium">
              {formatearImporte(saldoDeFila(fila), fila.moneda, "")}
            </Td>
            {hayColumna && (
              <Td derecha>
                <span className="flex flex-wrap justify-end gap-2">
                  {/*
                    ⚠ Sin acción en una fila saldada: no está deshabilitada, no está.
                    Un botón gris invita a pulsarlo y devuelve un error que no explica
                    nada, porque no hay nada que cobrar.
                  */}
                  {ofreceAccion && !fila.saldada && !fila.anulada && fila.accion !== null && (
                    <button
                      type="button"
                      data-testid={`accion-${fila.id}`}
                      onClick={() => onAccion?.(fila.id)}
                      className="foco-cristal rounded-[var(--radius-control)] border px-2 py-1 text-xs"
                      style={{ borderColor: "var(--border)" }}
                    >
                      {fila.accion ?? accion}
                    </button>
                  )}
                  {/* La secundaria sí en una fila saldada, y no en una anulada: una
                      cuenta que ya no existe no necesita que le justifiquen nada. */}
                  {ofreceSecundaria && !fila.anulada && (
                    <button
                      type="button"
                      data-testid={`secundaria-${fila.id}`}
                      onClick={() => onAccionSecundaria?.(fila.id)}
                      className="foco-cristal rounded-[var(--radius-control)] border px-2 py-1 text-xs"
                      style={{ borderColor: "var(--border)" }}
                    >
                      {accionSecundaria}
                    </button>
                  )}
                </span>
              </Td>
            )}
          </Fila>
        ))}
      </Cuerpo>
    </Tabla>
  )
}
